import { Sparkles, Lock, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSubscription } from '@/store/subscription';
import { useWorkspaceStore } from '@/store/workspace';
import { track } from '@/lib/posthog';

interface UpgradePromptProps {
  type: 'ai' | 'project-limit';
  currentProjectCount?: number;
  projectLimit?: number;
  onDismiss?: () => void;
}

export function UpgradePrompt({
  type,
  currentProjectCount = 0,
  projectLimit = 3,
  onDismiss,
}: UpgradePromptProps) {
  const { createCheckoutSession } = useSubscription();
  const workspaceName = useWorkspaceStore(s => s.currentWorkspace?.name);
  const priceId = import.meta.env.VITE_STRIPE_PRO_PRICE_ID;

  const handleUpgrade = async () => {
    if (!priceId) {
      console.error('VITE_STRIPE_PRO_PRICE_ID not configured');
      return;
    }

    track('upgrade_clicked', { prompt_type: type });
    const url = await createCheckoutSession(priceId);
    if (url) {
      window.location.href = url;
    }
  };

  const workspaceLabel = workspaceName ? ` ${workspaceName}` : '';
  const content = type === 'ai' ? {
    icon: <Sparkles className="h-8 w-8 text-purple-500" />,
    title: 'AI Features Require Pro',
    description: `Upgrade${workspaceLabel} to unlock AI-powered document analysis, automatic project extraction, and smart insights.`,
    features: [
      'AI-powered document analysis',
      'Automatic milestone & risk extraction',
      'Smart project insights',
      'Unlimited projects',
    ],
  } : {
    icon: <Lock className="h-8 w-8 text-amber-500" />,
    title: 'Project Limit Reached',
    description: `${workspaceName ? `${workspaceName} has` : "You've"} reached the ${projectLimit} project limit on the free tier. Upgrade to Pro for unlimited projects.`,
    features: [
      'Unlimited projects',
      'AI-powered features',
      'Priority support',
      'Advanced analytics',
    ],
  };

  return (
    <Card className="w-full max-w-md mx-auto border-2 border-ingesta-blue-200">
      <CardHeader className="text-center pb-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-ingesta-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
          {content.icon}
        </div>
        <CardTitle className="text-xl font-bold text-gray-900">
          {content.title}
        </CardTitle>
        <CardDescription className="text-base">
          {content.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {type === 'project-limit' && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-amber-600">{currentProjectCount}</span>
            <span>of</span>
            <span className="font-semibold">{projectLimit}</span>
            <span>projects used</span>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Pro includes:</h4>
          <ul className="space-y-2">
            {content.features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-3 h-3 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            className="w-full"
            size="lg"
            onClick={handleUpgrade}
          >
            <Rocket className="h-4 w-4 mr-2" />
            Upgrade to Pro - $2.99/month
          </Button>

          <p className="text-xs text-center text-gray-500">
            7-day free trial included. Cancel anytime.
          </p>

          {onDismiss && (
            <Button
              variant="ghost"
              className="w-full text-gray-500"
              onClick={onDismiss}
            >
              Maybe later
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Inline upgrade prompt for use in smaller spaces
 */
export function InlineUpgradePrompt({
  type,
  onUpgrade,
}: {
  type: 'ai' | 'project-limit';
  onUpgrade?: () => void;
}) {
  const { createCheckoutSession } = useSubscription();
  const priceId = import.meta.env.VITE_STRIPE_PRO_PRICE_ID;

  const handleUpgrade = async () => {
    track('upgrade_clicked', { prompt_type: type, variant: 'inline' });
    if (onUpgrade) {
      onUpgrade();
      return;
    }

    if (!priceId) {
      console.error('VITE_STRIPE_PRO_PRICE_ID not configured');
      return;
    }

    const url = await createCheckoutSession(priceId);
    if (url) {
      window.location.href = url;
    }
  };

  const message = type === 'ai'
    ? 'AI features require a Pro subscription.'
    : 'Upgrade to Pro for unlimited projects.';

  return (
    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
      <div className="flex items-center gap-2">
        {type === 'ai' ? (
          <Sparkles className="h-4 w-4 text-amber-600" />
        ) : (
          <Lock className="h-4 w-4 text-amber-600" />
        )}
        <span className="text-sm text-amber-800">{message}</span>
      </div>
      <Button size="sm" onClick={handleUpgrade}>
        Upgrade
      </Button>
    </div>
  );
}
